import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { usePagination } from '../hooks/usePagination';
import { apiList, apiDelete, apiCreate, apiUpdate } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { UserForm } from '../components/forms/UserForm';
import { useAuth } from '../hooks/useAuth';
import { ActionMenu } from '../components/ui/ActionMenu';
import { refreshAllCounters } from '../utils/refreshCounters';
import { CountBand } from '../components/ui/CountBand';
import { FilterPills } from '../components/ui/FilterPills';

const roleBadgeColor: Record<string, string> = {
  admin: 'red',
  super_admin: 'red',
  integrator: 'blue',
  dealer_admin: 'blue',
  customer: 'green',
  customer_admin: 'green',
  property_manager: 'purple',
};

const roleLabel: Record<string, string> = {
  admin: 'Admin',
  super_admin: 'Super Admin',
  integrator: 'Dealer',
  dealer_admin: 'Dealer Admin',
  customer: 'Customer',
  customer_admin: 'Customer Admin',
  property_manager: 'Property Manager',
};

export const WebUsers: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { selected, toggle, toggleAll, clear: clearSelection } = useSelection();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const { user: currentUser } = useAuth();

  const searchFields = useCallback((u: any) => [
    u.first_name,
    u.last_name,
    u.email,
    u.role,
    u.phone,
  ], []);

  const userFilter = useCallback((u: any) => roleFilter === 'all' || String(u.role || '').toLowerCase() === roleFilter, [roleFilter]);
  const { search, setSearch, page, setPage, totalPages, paginatedData, totalFiltered, startIndex, endIndex } = usePagination(users, searchFields, userFilter);

  const load = useCallback(async () => {
    try {
      const userData = await apiList('users');
      setUsers(userData.map((u: any) => ({
        ...u,
        full_name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.id}`,
      })));
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleBulkDelete = async (ids: number[]) => {
    await Promise.all(ids.map(id => apiDelete('users', id)));
    refreshAllCounters();
    setLoading(true);
    await load();
  };

  const handleCreateUser = async (data: any) => {
    await apiCreate('users', data);
    refreshAllCounters();
    setCreateModalOpen(false);
    setLoading(true);
    await load();
  };

  const handleUpdateUser = async (data: any) => {
    if (!editingUser) return;
    const payload = { ...data };
    if (!payload.password) delete payload.password;
    await apiUpdate('users', editingUser.id, payload);
    refreshAllCounters();
    setEditingUser(null);
    setLoading(true);
    await load();
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm('Remove this user? This cannot be undone.')) return;
    await apiDelete('users', id);
    refreshAllCounters();
    setLoading(true);
    await load();
  };

  const columns = [
    {
      key: '_select',
      header: (
        <input
          type="checkbox"
          checked={selected.size === users.length && users.length > 0}
          onChange={() => toggleAll(users.map(u => u.id))}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      render: (u: any) => (
        <input
          type="checkbox"
          checked={selected.has(u.id)}
          onChange={() => toggle(u.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        />
      ),
      className: 'w-8'
    },
    {
      key: 'full_name',
      header: 'Name',
      render: (u: any) => (
        <div>
          <p className="font-medium text-gray-900">{u.full_name}</p>
          {u.phone && <p className="text-xs text-gray-500">{u.phone}</p>}
        </div>
      )
    },
    {
      key: 'email',
      header: 'Email',
      render: (u: any) => u.email || '—'
    },
    {
      key: 'role',
      header: 'Role',
      render: (u: any) => (
        <Badge color={roleBadgeColor[u.role] || 'gray'}>
          {roleLabel[u.role] || u.role}
        </Badge>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (u: any) => (
        <Badge status={u.status || 'active'}>
          {u.status || 'active'}
        </Badge>
      )
    },
    {
      key: 'last_login',
      header: 'Last Active',
      render: (u: any) => u.last_login ? formatDate(u.last_login) : <span className="text-gray-400">Never</span>
    },
    {
      key: 'created_at',
      header: 'Created At',
      render: (u: any) => u.created_at ? formatDate(u.created_at) : '—'
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (u: any) => {
        const isSelf = currentUser && u.id === currentUser.id;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <ActionMenu items={[
              { label: 'Edit', onClick: () => setEditingUser(u) },
              ...(isSelf ? [] : [{ label: 'Delete', variant: 'danger' as const, onClick: () => handleDeleteUser(u.id) }]),
            ]} />
          </div>
        );
      },
      className: 'w-24',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="shadow-list-page has-prototype-toolbar">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Web Users</h1>
          <p className="text-gray-500 mt-1">Manage all web application users</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          Add User
        </Button>
      </div>
      <CountBand value={totalFiltered} detail="Web users visible to this account" />
      <div className="prototype-toolbar prototype-toolbar-pill-row"><SearchInput value={search} onChange={setSearch} placeholder="Search users..."/><FilterPills ariaLabel="Filter users by role" value={roleFilter} onChange={setRoleFilter} options={[{value:'all',label:'All'},{value:'super_admin',label:'Super Admin'},{value:'admin',label:'Admin'},{value:'integrator',label:'Dealer'},{value:'customer',label:'Customer'},{value:'property_manager',label:'Property Manager'}]}/><Button onClick={()=>setCreateModalOpen(true)}>Add User</Button></div>
      {/* Mobile cards */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2">
          {paginatedData.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No users found.</div>
          ) : paginatedData.map((u: any) => (
            <div key={u.id} className="bg-white rounded-xl border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 capitalize">{(u.role || 'user').replace('_', ' ')}</span>
              </div>
              {u.last_login_at && <p className="text-[11px] text-gray-500 mt-1">Last login: {new Date(u.last_login_at).toLocaleDateString()}</p>}
              <div className="mobile-card-actions">
                <button onClick={() => setEditingUser(u)} className="row-open">Edit <span aria-hidden="true">→</span></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop table */}
      <Card padding={false} className="hidden md:block">
        <div className="p-4 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search users by name, email, or role..."
          />
        </div>
        <Table
          columns={columns}
          data={paginatedData}
          emptyMessage="No users found"
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={totalFiltered}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setPage}
        />
      </Card>
      <BulkActions
        selectedIds={selected}
        onClearSelection={clearSelection}
        onDelete={handleBulkDelete}
        entityName="users"
      />

      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} title="Add User">
        <UserForm
          onSubmit={handleCreateUser}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      <Modal open={!!editingUser} onClose={() => { setEditingUser(null); setResetMsg(null); }} title={editingUser ? `Edit ${editingUser.first_name || editingUser.email || 'user'}` : 'Edit User'}>
        {editingUser && (
          <div className="space-y-4">
            <UserForm
              initialData={editingUser}
              onSubmit={handleUpdateUser}
              onCancel={() => setEditingUser(null)}
            />
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <p className="text-xs font-semibold text-gray-700">Account actions</p>
              <Button
                size="sm"
                disabled={resetting}
                onClick={async () => {
                  if (!editingUser) return;
                  if (!window.confirm(`Reset password for ${editingUser.email}? A new random password will be generated and shown once.`)) return;
                  setResetting(true);
                  setResetMsg(null);
                  try {
                    const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                    const tk = localStorage.getItem('intrlock_token') || '';
                    const alpha = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
                    const pw = Array.from({ length: 12 }, () => alpha[Math.floor(Math.random() * alpha.length)]).join('');
                    const res = await fetch(`${apiBase}/auth/set-password`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                      body: JSON.stringify({ user_id: editingUser.id, password: pw }),
                    });
                    if (!res.ok) throw new Error(await res.text());
                    setResetMsg(`New password: ${pw}  (copy it now — it won't be shown again)`);
                  } catch (e: any) {
                    setResetMsg('Reset failed: ' + (e?.message ?? e));
                  } finally {
                    setResetting(false);
                  }
                }}
              >
                {resetting ? 'Resetting…' : 'Reset password'}
              </Button>
              {resetMsg && (
                <p className="text-xs mt-2 text-gray-700 break-all">{resetMsg}</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
